import { Component, Element, Prop, h } from '@stencil/core';

import { actions as answerActions } from '../actions/actions.answers';
import {
  checkShowLegalAcceptance,
  checkShowReplacementVehicle,
  onContactHydratorInit,
  onContactVehicleSave,
  onFetchVoiVinData,
  onRVAnswerChange,
  onReciveReplacementVehicleMakeModels,
} from '../actions/actions.customer';
import { onReceiveDealerRepInfo } from '../actions/actions.dealer';
import { actions as pageParamsActions } from '../actions/actions.page-params';
import { onQuestionsInit } from '../actions/actions.questions';
import { ReplacementVehicleOptions } from '../interfaces/interface.lookup';
import { state as answerState, on as onAnswerStateChange } from '../store/store.answers';
import { state as offerState } from '../store/store.offer';
import { on as onSupplementStateChange, state as supplementState } from '../store/store.supplement';
import { on as onValidationStateChange, state as validationState } from '../store/store.validation';
import { onEngagementInit } from '../tracking/tracking.asc';
import { trackLeadFormLoad } from '../tracking/tracking.leadForm';
import { ANSWER_LEAD_TYPE_CASH_OFFER, getReplacementVehicleProperties } from '../utils/utils.answers';
import { ENGAGEMENT_FORMS } from '../utils/utils.asc-analytics';
import { QUESTION_LEAD_TYPE } from '../utils/utils.question';

@Component({
  tag: 'contact-state-hydrator',
})
export class ContactStateHydrator {
  @Element() el: HTMLElement;

  // Vehicle
  @Prop() colorId: number = 0;
  @Prop() color: string = '';
  @Prop() drivetrainId: number = 0;
  @Prop() drivetrain: string = '';
  @Prop() engineId: number = 0;
  @Prop() engine: string = '';
  @Prop() makeId: number = 0;
  @Prop() make: string = '';
  @Prop() mileage: number = 0;
  @Prop() modelId: number = 0;
  @Prop() model: string = '';
  @Prop() transmissionId: number = 0;
  @Prop() transmission: string = '';
  @Prop() trimId: number = 0;
  @Prop() trim: string = '';
  @Prop() vin: string = '';
  @Prop() yearId: number = 0;
  @Prop() year: number = 0;
  @Prop() selectedOptionIds: string = '';
  @Prop() unselectedOptionIds: string = '';
  @Prop() referrerUrl: string = '';
  @Prop() browserType: string = '';
  @Prop() includePixall: boolean = false;
  @Prop() pixallId: string = '';
  @Prop() pixallRealId: string = '';

  // Replacement Vehicle
  @Prop() replacementVehicleMakes?: [];
  @Prop() replacementVehicleModels?: [];
  @Prop() replacementVehicleTypes?: [];

  @Prop() replacementVehicleOptions?: ReplacementVehicleOptions = null;
  // Analytics
  @Prop() entry: string;
  @Prop() offerCode: string;
  @Prop() lnxCode: string;
  @Prop() dealerGuid: string;
  @Prop() cobrandingGroupId: string;
  @Prop() category: string;
  @Prop() theme: string;

  // Contact info
  @Prop() contactFirstName: string = '';
  @Prop() contactLastName: string = '';
  @Prop() contactPhone: string = '';
  @Prop() contactEmail: string = '';
  @Prop() contactZip: string = '';
  @Prop() contactAdditionalComment: string = '';
  @Prop() showLegalAcceptance: boolean = true;
  @Prop() showLegalText: boolean = true;

  @Prop() isConsumerOffer: boolean = true;
  @Prop() customId: string = '';

  // Dealer info
  @Prop() dealerName: string = '';
  @Prop() dealerRepId: number;
  @Prop() dealerRepName: string = '';
  @Prop() inviteId: string = '';
  @Prop() authToken: string = '';

  @Prop() emailCustomerCheckbox: number = 0; // default to 0 if param is not passed
  @Prop() showFormValidation: boolean = false;
  @Prop() acceptsRv: boolean = true;
  @Prop() voiVin: string;
  @Prop() answerValueForLeadTypeNo: string = ANSWER_LEAD_TYPE_CASH_OFFER;
  @Prop() notifyReplacementVehicleAnswerChange: boolean;
  @Prop() allowIneligibleVehicleSubmission: boolean = false;

  @Prop() showAdditionalComment: boolean = false;
  @Prop() isKelleyBuysYourCarRequested: boolean = false;
  @Prop() isKelleyBuysYourCarFakeDoor: boolean = false;
  @Prop() isInstantCashOfferRequested: boolean = true;
  @Prop() overrideHeaderText: string = '';
  @Prop() overrideSubHeaderText: string = '';
  // TODO: Some of what is in here should be pulled up to actions layer

  async componentWillUpdate() {
    this.updateStateProps('showFormValidation', this.showFormValidation);

    if (this.replacementVehicleOptions) {
      onReciveReplacementVehicleMakeModels(this.replacementVehicleOptions);
    }
  }

  async componentWillLoad() {
    // boostrap props
    pageParamsActions.onReceivedParams({ entry: this.entry, offerCode: this.offerCode ? this.offerCode.toUpperCase() : undefined, lnxCode: this.lnxCode, category: this.category });
    answerActions.setConsumerOffer(this.isConsumerOffer);

    if (this.dealerRepId) {
      onReceiveDealerRepInfo(this.dealerRepId, this.dealerRepName, this.inviteId, this.authToken);
    }

    const vehicle = {
      yearId: this.yearId,
      year: this.year,
      makeId: this.makeId,
      make: this.make,
      modelId: this.modelId,
      model: this.model,
      trimId: this.trimId,
      trim: this.trim,
      colorId: this.colorId,
      color: this.color,
      transmissionId: this.transmissionId,
      transmission: this.transmission,
      engineId: this.engineId,
      engine: this.engine,
      drivetrainId: this.drivetrainId,
      drivetrain: this.drivetrain,
      vin: this.vin,
      mileage: this.mileage,
    };

    onEngagementInit(ENGAGEMENT_FORMS.customerContact, false);
    await onQuestionsInit(vehicle, this.allowIneligibleVehicleSubmission);
    trackLeadFormLoad();

    checkShowLegalAcceptance(this.showLegalAcceptance);
    this.setupStateProps();

    const contactProps = {
      firstName: this.contactFirstName,
      lastName: this.contactLastName,
      phone: this.contactPhone,
      email: this.contactEmail,
      zip: this.contactZip,
      emailCustomerCheckbox: this.emailCustomerCheckbox,
    };

    const integrationProps = {
      referrerUrl: this.referrerUrl,
      includePixall: this.includePixall,
      pixallId: this.pixallId,
      pixallRealId: this.pixallRealId,
      browserType: this.browserType,
      offerCode: this.offerCode ? this.offerCode.toUpperCase() : undefined,
      lnxCode: this.lnxCode,
      dealerGuid: this.dealerGuid,
      cobrandingGroupId: this.cobrandingGroupId ?? '',
      customId: this.customId ?? '',
    };

    onFetchVoiVinData(this.voiVin, supplementState.makesModels.values, this.acceptsRv, this.dealerGuid);
    onValidationStateChange('set', (prop, value) => {
      if (prop === 'form') {
        this.updateStateProps('formErrors', value.contactInfo);
      }
      if (prop === 'fields') {
        this.updateStateProps('fieldErrors', value.contactInfo);
      }
    });

    onSupplementStateChange('set', (prop, value) => {
      if (prop === 'makesModels') {
        for (const [k, v] of Object.entries(this.getMakesModelsProps(value))) {
          this.updateStateProps(k, v);
        }
      }
      if (prop === 'yearRange') {
        for (const [k, v] of Object.entries(this.getYearRangeProps(value))) {
          this.updateStateProps(k, v);
        }
      }
      if (['rvLookingForOptions', 'replacementVehicleOptions'].includes(prop)) {
        this.updateStateProps(prop, value);
      }
    });

    onAnswerStateChange('set', (prop, value) => {
      if (prop === 'contactInfo') {
        for (const [k, v] of Object.entries(this.getContactInfoProps(value))) {
          this.updateStateProps(k, v);
        }
      }
      if (prop === 'replacementVehicle') {
        onRVAnswerChange(this.notifyReplacementVehicleAnswerChange, value, supplementState.makesModels.values);
        for (const [k, v] of Object.entries(getReplacementVehicleProperties(value))) {
          this.updateStateProps(k, v);
        }
      }
    });

    onContactHydratorInit(contactProps, integrationProps);

    if (this.replacementVehicleOptions) {
      onReciveReplacementVehicleMakeModels(this.replacementVehicleOptions);
    }

    onContactVehicleSave(vehicle, this.selectedOptionIds, this.unselectedOptionIds);
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    const contactNode = this.el.querySelector('contact-information');
    contactNode[prop] = value;
  }

  // explicitly declare initial state props
  setupStateProps() {
    const contactNode = this.el.querySelector('contact-information');
    contactNode['isConsumerOffer'] = this.isConsumerOffer;

    const { offerError } = offerState;
    contactNode['formErrors'] = validationState.form.contactInfo;
    contactNode['fieldErrors'] = validationState.fields.contactInfo;
    contactNode['showFormValidation'] = this.showFormValidation || !!offerError;

    // contactInfo
    const contactInfo = this.getContactInfoProps(answerState.contactInfo);
    contactNode['contactFirstName'] = contactInfo.contactFirstName;
    contactNode['contactLastName'] = contactInfo.contactLastName;
    contactNode['contactEmail'] = contactInfo.contactEmail;
    contactNode['contactPhone'] = contactInfo.contactPhone;
    contactNode['contactZip'] = contactInfo.contactZip;
    contactNode['emailCustomerCheckbox'] = contactInfo.contactEmailCustomerCheckbox;

    contactNode['showLegalAcceptance'] = this.showLegalAcceptance;
    contactNode['showLegalText'] = this.showLegalText;

    // dealerInfo
    contactNode['dealerName'] = this.dealerName;
    contactNode['dealerRepId'] = this.dealerRepId;
    contactNode['dealerRepName'] = this.dealerRepName;
    contactNode['inviteId'] = this.inviteId;
    contactNode['authToken'] = this.authToken;

    const showReplacementVehicleSection = checkShowReplacementVehicle(this.offerCode, this.acceptsRv);
    contactNode['showReplacementVehicleSection'] = showReplacementVehicleSection;
    contactNode['answerValueForLeadTypeNo'] = this.answerValueForLeadTypeNo;
    // theme
    contactNode['theme'] = this.theme;

    // additional comment
    contactNode['showAdditionalComment'] = this.showAdditionalComment;
    contactNode['contactAdditionalComment'] = this.contactAdditionalComment;

    // disposal info
    contactNode['isInstantCashOfferRequested'] = this.isInstantCashOfferRequested;
    contactNode['isKelleyBuysYourCarRequested'] = this.isKelleyBuysYourCarRequested;
    contactNode['isKelleyBuysYourCarFakeDoor'] = this.isKelleyBuysYourCarFakeDoor;

    if (showReplacementVehicleSection) {
      // replacementVehicle
      const replacementVehicle = getReplacementVehicleProperties(answerState.replacementVehicle);
      contactNode['replacementIsLookingForReplacementVehicle'] = replacementVehicle.replacementIsLookingForReplacementVehicle;
      contactNode['replacementIsLookingForNewCars'] = replacementVehicle.replacementIsLookingForNewCars;
      contactNode['replacementIsLookingForCertifiedCars'] = replacementVehicle.replacementIsLookingForCertifiedCars;
      contactNode['replacementIsLookingForUsedCars'] = replacementVehicle.replacementIsLookingForUsedCars;
      contactNode['replacementMinYear'] = replacementVehicle.replacementMinYear;
      contactNode['replacementMaxYear'] = replacementVehicle.replacementMaxYear;
      contactNode['replacementMakeModelId'] = replacementVehicle.replacementMakeModelId;
      contactNode['replacementMakeId'] = replacementVehicle.replacementMakeId;

      // supplement
      contactNode['replacementYearRangeMin'] = supplementState.yearRange.values.find((r) => r.id === 'yearRange/min')?.label;
      contactNode['replacementYearRangeMax'] = supplementState.yearRange.values.find((r) => r.id === 'yearRange/max')?.label;
      contactNode['replacementMakesModels'] = supplementState.makesModels.values;
      contactNode['rvLookingForOptions'] = supplementState.rvLookingForOptions;
      contactNode['replacementVehicleOptions'] = supplementState.replacementVehicleOptions;

      // header text
      contactNode['overrideHeaderText'] = this.overrideHeaderText;
      contactNode['overrideSubHeaderText'] = this.overrideSubHeaderText;
    } else {
      answerActions.onQuestionAnswered(QUESTION_LEAD_TYPE, this.answerValueForLeadTypeNo);
    }
  }

  getContactInfoProps(contactInfo) {
    const result = {
      contactFirstName: contactInfo.firstName,
      contactLastName: contactInfo.lastName,
      contactEmail: contactInfo.email,
      contactPhone: contactInfo.phone,
      contactZip: contactInfo.zip,
      contactLegalAcceptance: contactInfo.legalAcceptance,
      contactEmailCustomerCheckbox: contactInfo.emailCustomerCheckbox,
    };
    return result;
  }

  getYearRangeProps(yearRange) {
    const result = {
      replacementYearRangeMin: yearRange.values.find((r) => r.id === 'yearRange/min')?.label,
      replacementYearRangeMax: yearRange.values.find((r) => r.id === 'yearRange/max')?.label,
    };
    return result;
  }

  getMakesModelsProps(makesModels) {
    const result = {
      replacementMakesModels: makesModels.values,
    };
    return result;
  }

  render() {
    return (
      <div class="hydration-state hydration-state--contact" data-testId="contactStateHydrator">
        <slot />
      </div>
    );
  }
}
